import type { SVGProps } from 'react';

/**
 * The app shell's icon set: simple 24×24 line drawings, drawn for this
 * project rather than pulled from an icon font or package. One stroke weight,
 * round caps and joins, `currentColor`, so an icon takes the color of the
 * text beside it and reads the same in every state (hover, current page,
 * muted).
 *
 * Decorative by default (`aria-hidden`): the link or button around an icon
 * carries the accessible name, never the drawing.
 */
const PATHS = {
  // Navigation destinations.
  home: 'M3 11.5 12 4l9 7.5M5.5 9.5V20h5v-5.5h3V20h5V9.5',
  users:
    'M9 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7ZM2.5 20c.6-3.4 3.3-5.5 6.5-5.5s5.9 2.1 6.5 5.5M16 4.3a3.5 3.5 0 0 1 0 6.4M18.5 14.8c1.6.8 2.7 2.6 3 5.2',
  message:
    'M5.5 4h13A1.5 1.5 0 0 1 20 5.5v9a1.5 1.5 0 0 1-1.5 1.5H10l-4.5 4v-4A1.5 1.5 0 0 1 4 14.5v-9A1.5 1.5 0 0 1 5.5 4ZM8 8.5h8M8 12h5',
  quote: 'M6 3h8l4 4v14H6zM14 3v4h4M9 11h6M9 14.5h6M9 18h3.5',
  team: 'M12 10.5a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM6.5 20c.4-3 2.7-5 5.5-5s5.1 2 5.5 5M5.5 11.5a2.25 2.25 0 1 0 0-4.5M18.5 11.5a2.25 2.25 0 1 0 0-4.5M2 18c.3-1.9 1.4-3.2 3-3.6M22 18c-.3-1.9-1.4-3.2-3-3.6',
  calendar:
    'M4 6.5A1.5 1.5 0 0 1 5.5 5h13A1.5 1.5 0 0 1 20 6.5v12a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 4 18.5ZM4 10h16M8.5 3v4M15.5 3v4',
  chats:
    'M4.5 4h9A1.5 1.5 0 0 1 15 5.5v6a1.5 1.5 0 0 1-1.5 1.5H8.5L5 16v-3h-.5A1.5 1.5 0 0 1 3 11.5v-6A1.5 1.5 0 0 1 4.5 4ZM18 8.5h1.5A1.5 1.5 0 0 1 21 10v6a1.5 1.5 0 0 1-1.5 1.5H19V20l-3.5-2.5h-4A1.5 1.5 0 0 1 10 16v-.5',
  // A calendar with a hand-over: a visit passed from one person to another.
  coverage:
    'M4 6.5A1.5 1.5 0 0 1 5.5 5h13A1.5 1.5 0 0 1 20 6.5v12a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 4 18.5ZM8.5 3v4M15.5 3v4M8 12h7.5l-2-2M16 16.5H8.5l2 2',
  // Four tiles: the Work hub, a page of the office's other tools.
  tools: 'M4.5 4.5h6v6h-6zM13.5 4.5h6v6h-6zM4.5 13.5h6v6h-6zM13.5 13.5h6v6h-6z',
  inspection:
    'M9 3.5h6v3H9zM9 5H6.5A1.5 1.5 0 0 0 5 6.5v13A1.5 1.5 0 0 0 6.5 21h11a1.5 1.5 0 0 0 1.5-1.5v-13A1.5 1.5 0 0 0 17.5 5H15M8.5 13.5l2.5 2.5 4.5-5',
  change: 'M7 4 3.5 7.5 7 11M3.5 7.5H17M17 13l3.5 3.5L17 20M20.5 16.5H7',
  invoice: 'M6 3h12v18l-2-1.5-2 1.5-2-1.5-2 1.5-2-1.5L6 21ZM9 8h6M9 11.5h6M9 15h3.5',
  chart: 'M4 20V4M4 20h16M8 16v-4M12 16V8M16 16v-6',
  history: 'M4.6 15a8 8 0 1 0 .9-7.5M4 4v4h4M12 8v4.5l3 2',
  feedback: 'M12 3a6 6 0 0 0-3.5 10.9V16h7v-2.1A6 6 0 0 0 12 3ZM9.5 19h5M10.5 21.5h3',
  settings:
    'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM19.4 13.5l1.6 1.2-2 3.4-1.9-.7a7 7 0 0 1-1.8 1l-.3 2h-4l-.3-2a7 7 0 0 1-1.8-1l-1.9.7-2-3.4 1.6-1.2a7 7 0 0 1 0-3L3 9.3l2-3.4 1.9.7a7 7 0 0 1 1.8-1l.3-2h4l.3 2a7 7 0 0 1 1.8 1l1.9-.7 2 3.4-1.6 1.2a7 7 0 0 1 0 3Z',
  help: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18ZM9.5 9a2.5 2.5 0 1 1 4.3 1.7c-1.1.6-1.8 1.1-1.8 2.3M12 16.5h.01',
  // The partner portal and the operator console.
  shield: 'M12 3 5 6v5.5c0 4.3 2.9 8 7 9.5 4.1-1.5 7-5.2 7-9.5V6ZM9 12l2 2 4-4',
  tag: 'M3.5 12.5V4.5a1 1 0 0 1 1-1h8l8 8-9 9ZM8.5 8.5h.01',
  impersonate:
    'M9 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7ZM2.5 20c.6-3.4 3.3-5.5 6.5-5.5 1.4 0 2.7.4 3.8 1.1M15 17h6.5M18.5 14l3 3-3 3',
  monitor:
    'M3.5 5.5A1.5 1.5 0 0 1 5 4h14a1.5 1.5 0 0 1 1.5 1.5v9A1.5 1.5 0 0 1 19 16H5a1.5 1.5 0 0 1-1.5-1.5ZM9 20h6M12 16v4M10.5 8v4l3.5-2Z',
  // Shell controls.
  bell: 'M6 16V11a6 6 0 1 1 12 0v5l1.5 2h-15ZM10 20.5a2.2 2.2 0 0 0 4 0',
  more: 'M5 12h.01M12 12h.01M19 12h.01',
  close: 'M6 6l12 12M18 6 6 18',
  search: 'M10.5 17.5a7 7 0 1 0 0-14 7 7 0 0 0 0 14ZM20.5 20.5l-5-5',
  chevronDown: 'm6 9 6 6 6-6',
  check: 'm5 12.5 4.5 4.5L19 7.5',
  pin: 'M12 21s-6.5-5.6-6.5-11a6.5 6.5 0 1 1 13 0c0 5.4-6.5 11-6.5 11ZM12 12.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z',
  company: 'M4 20V9l8-5 8 5v11M9 20v-6h6v6M2 20h20',
  signOut: 'M15 4h3.5A1.5 1.5 0 0 1 20 5.5v13a1.5 1.5 0 0 1-1.5 1.5H15M10 16.5 14.5 12 10 7.5M14.5 12H4',
} as const;

export type IconName = keyof typeof PATHS;

export function Icon({ name, size = 20, ...rest }: { name: IconName; size?: number } & SVGProps<SVGSVGElement>) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      <path d={PATHS[name]} />
    </svg>
  );
}
